import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, Factory } from "@phosphor-icons/react";

const CONSISTENCY = {
  Consistent: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  Inconsistent: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
};

export const ModelCard = ({ model, selected, busy, onSelect }) => (
  <button
    type="button"
    disabled={busy}
    onClick={() => onSelect(model)}
    data-testid={`routing-model-${model.model_id}`}
    className={`w-full text-left rounded-lg border p-3 transition-colors duration-150 disabled:opacity-60 ${
      selected
        ? "border-[#0E7C86] bg-[#F0FAFA] ring-1 ring-[#0E7C86]"
        : "border-[#D0D5DD] bg-white hover:border-[#98A2B3] hover:bg-[#FCFCFD]"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Factory size={15} className="text-[#0E7C86] shrink-0" />
          <span className="font-heading text-[14px] font-semibold truncate">{model.model_id}</span>
        </div>
        <p className="text-[12px] text-[#475467] mt-1 truncate">{model.product_description || "\u2014"}</p>
        <p className="text-[11px] text-[#98A2B3] mt-0.5">
          Valid {model.validity_start} to {model.validity_end}
        </p>
      </div>
      <Badge
        variant="outline"
        className={`shrink-0 text-[10px] ${CONSISTENCY[model.planning_consistency_label] || "bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]"}`}
      >
        {model.consistent ? <CheckCircle size={11} className="mr-1" /> : <WarningCircle size={11} className="mr-1" />}
        {model.planning_consistency_label}
      </Badge>
    </div>
  </button>
);

export const ModelPicker = ({ products, query, onQuery, onPickProduct, productId, models, loadingModels, selectedModel, busy, onSelectModel }) => (
  <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
    <div>
      <label className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide">Part</label>
      <input
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        placeholder="Search a part, e.g. PL-0037A"
        data-testid="routing-product-search"
        className="mt-1 w-full h-9 rounded-lg border border-[#D0D5DD] px-3 text-[13px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors"
      />
      {products.length > 0 && (
        <div className="mt-1 rounded-lg border border-[#D0D5DD] bg-white max-h-56 overflow-y-auto divide-y divide-[#F2F4F7]">
          {products.map((p) => (
            <button
              key={p.product_id}
              type="button"
              onClick={() => onPickProduct(p)}
              data-testid={`routing-product-${p.product_id}`}
              className="w-full text-left px-3 py-2 hover:bg-[#F9FAFB] transition-colors"
            >
              <div className="text-[13px] font-medium">{p.product_id}</div>
              <div className="text-[11px] text-[#667085] truncate">{p.description}</div>
            </button>
          ))}
        </div>
      )}
      {productId && (
        <p className="mt-2 text-[12px] text-[#475467]" data-testid="routing-selected-product">
          Selected part <span className="font-semibold text-[#1D2939]">{productId}</span>
        </p>
      )}
    </div>

    <div>
      <label className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide">
        Production model {productId ? `for ${productId}` : ""}
      </label>
      {!productId && <p className="mt-2 text-[13px] text-[#98A2B3]">Pick a part first.</p>}
      {productId && loadingModels && <p className="mt-2 text-[13px] text-[#475467]">Looking up production models…</p>}
      {productId && !loadingModels && models.length === 0 && (
        <p className="mt-2 text-[13px] text-[#B42318]" data-testid="routing-no-models">
          SAP has no production model for this part. One must exist before a routing can be attached to it.
        </p>
      )}
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="routing-model-list">
        {models.map((m) => (
          <ModelCard
            key={m.model_id}
            model={m}
            selected={selectedModel?.model_id === m.model_id}
            busy={busy}
            onSelect={onSelectModel}
          />
        ))}
      </div>
    </div>
  </div>
);

export const ContextSummary = ({ context, onDownload, downloading }) => {
  if (!context) return null;
  const { model, item_groups: groups, resources } = context;
  return (
    <div className="rounded-lg border border-[#D0D5DD] bg-white p-4" data-testid="routing-context-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          ["Site", model.site_id],
          ["Bill of material", model.bill_of_material_id],
          ["Components to place", String(groups.length)],
          ["Resources available", String(resources.length)],
        ].map(([label, value]) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-[#98A2B3]">{label}</div>
            <div className="text-[14px] font-semibold text-[#1D2939] mt-0.5">{value || "\u2014"}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {groups.map((g) => (
          <Badge key={g.item_group_id} variant="outline" className="text-[10px] bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]">
            {g.label}
          </Badge>
        ))}
        {(context.byproducts || []).map((b) => (
          <Badge
            key={b.product_id}
            variant="outline"
            className="text-[10px] bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
            data-testid={`routing-context-byproduct-${b.product_id}`}
          >
            by-product {b.product_id}
          </Badge>
        ))}
      </div>
      <Button
        onClick={onDownload}
        disabled={downloading}
        data-testid="routing-download-template"
        className="mt-4 h-9 bg-[#0E7C86] hover:bg-[#0B656D] text-white text-[13px]"
      >
        {downloading ? "Preparing…" : "Download Excel template"}
      </Button>
      <p className="mt-2 text-[12px] text-[#667085]">
        The sheet arrives pre-filled with this model, its components and the resources valid at site {model.site_id}.
      </p>
    </div>
  );
};
